class Cart {
  static cart = {}

  static async addItem(payload) {
    try {
      if (!payload) {
        throw new Error('Payload is required')
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.description || error.message || 'Failed to add item to cart')
      }

      const data = await response.json()
      console.log('Item added:', data)

      // Esperamos el refresh: updateCart() refresca Cart.cart con un GET /cart.js.
      // Sin await, `await Cart.addItem(...)` resuelve con Cart.cart todavía stale y
      // los consumidores (ej. el botón del drawer de suscripción) leen un item_count viejo.
      await this.updateCart()

      return data
    } catch (error) {
      console.error('Cart error:', error)
      return
    }
  }

  static async clear() {
    try {
      const response = await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.description || error.message || 'Failed to clear cart')
      }

      const data = await response.json()
      console.log('Cart cleared:', data)

      await this.updateCart()

      return data
    } catch (error) {
      console.error('Cart error:', error)
      return
    }
  }

  static async get() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.description || error.message || 'Failed to get cart')
      }

      const data = await response.json()
      console.log('Cart retrieved:', data)

      return data
    } catch (error) {
      console.error('Cart error:', error)
      return
    }
  }

  static async update(payload) {
    try {
      if (!payload) {
        throw new Error('Payload is required')
      }

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.description || error.message || 'Failed to update cart')
      }

      const data = await response.json()
      console.log('Cart updated:', data)

      await this.updateCart()

      return data
    } catch (error) {
      console.error('Cart error:', error)
      return
    }
  }

  static async removeItem(key) {
    try {
      if (!key) {
        throw new Error('Key is required')
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.description || error.message || 'Failed to remove item from cart')
      }

      const data = await response.json()
      console.log('Item removed:', data)

      await this.updateCart()

      return data
    } catch (error) {
      console.error('Cart error:', error)
      return
    }
  }

  static async updateItem(payload) {
    try {
      if (!payload.id) {
        throw new Error('ID is required')
      }

      if (payload.quantity === undefined || payload.quantity === null) {
        throw new Error('Quantity is required')
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.description || error.message || 'Failed to update item in cart')
      }

      const data = await response.json()
      console.log('Item updated:', data)

      await this.updateCart()

      return data
    } catch (error) {
      console.error('Cart error:', error)
      return
    }
  }

  static async updateCart() {
    const newCart = await this.get()

    // get() devuelve undefined cuando /cart.js falla (red caída, timeout en mobile).
    // Antes lo asignábamos igual y this.cart quedaba en undefined: todo lo que deriva
    // del carrito pasaba a leer "carrito vacío". El efecto concreto era que el botón
    // "confirmar y pagar" de la suscripción se deshabilitaba (item_count 0) y NO se
    // recuperaba al volver la red, porque en el paso 3 nada vuelve a refrescar el
    // carrito: el cliente quedaba trabado sin poder pagar ni entender por qué.
    // Ante un fallo conservamos el último carrito conocido y no emitimos el evento,
    // porque el carrito no cambió: solo no pudimos leerlo.
    if (!newCart) {
      console.warn('[cart] no se pudo refrescar /cart.js; se mantiene el último carrito conocido')
      return this.cart
    }

    this.cart = newCart

    document.dispatchEvent(new CustomEvent("theme:cart:change", {
      detail: { cart: newCart },
      bubbles: true
    }))

    return newCart
  }

  // Regla única de qué entra en una suscripción. La consumen el popup
  // (subscription-popup.js) y los botones (subscription-cta-button.js); el
  // equivalente en Liquid vive en snippets/subscription-popup.liquid.
  // Quedan afuera:
  //  - los regalos de compra única (_threshold_gift / _coupon_gift): custom.js los
  //    da y los saca según el subtotal y los cupones, no son parte del pedido pago;
  //  - los packs, que Shopify marca como bundles con has_components. Un pack es una
  //    combinación fija de varios productos en una sola línea y no se puede renovar
  //    periódicamente, así que no puede formar parte de una suscripción.
  //    has_components solo existe en el line item de /cart.js: ni el objeto product
  //    de Liquid ni /products/<handle>.js exponen que un producto sea bundle.
  //
  // OJO: el item tiene que venir de /cart.js. Las respuestas de /cart/add.js y
  // /cart/change.js omiten has_components, así que un pack que venga de ahí pasa el
  // filtro como si fuera un producto simple. Ante un evento de carrito, releer /cart.js
  // en vez de confiar en el payload que trae el evento.
  static isSubscribableItem(item) {
    if (!item) return false

    const props = item.properties
    if (props && (props._threshold_gift || props._coupon_gift)) return false

    if (item.has_components) return false

    return true
  }

  // Unidades del carrito que sí forman parte de la suscripción. Es lo que define si
  // la suscripción está "vacía": con 0, no hay nada que suscribir.
  static getSubscribableItemCount(cart) {
    const items = (cart && cart.items) || []

    return items
      .filter(item => Cart.isSubscribableItem(item))
      .reduce((sum, item) => sum + (item.quantity || 0), 0)
  }

  static async getShippingRates(province, city, zip, address1) {

    let shippingParams = new URLSearchParams({
      'shipping_address[country]': 'Argentina',
      'shipping_address[province]': province ?? 'Buenos Aires',
      'shipping_address[city]': city ?? 'Ciudad Autónoma de Buenos Aires',
      'shipping_address[zip]': zip ?? '1000',
      'shipping_address[address1]': address1 ?? 'Calle Test 123',
      'include_carrier_rates': 'true'
    })

    try {
      await fetch('/cart/prepare_shipping_rates.json' + '?' + shippingParams, {
        method: 'POST'
      })

      shippingParams = new URLSearchParams({
        'shipping_address[country]': 'Argentina',
        'shipping_address[province]': province ?? 'Buenos Aires',
        'shipping_address[city]': city ?? 'Ciudad Autónoma de Buenos Aires',
        'shipping_address[zip]': zip ?? '1000',
        'shipping_address[address1]': address1 ?? 'Calle Test 123'
      })

      // async_shipping_rates devuelve shipping_rates: null mientras el cálculo
      // sigue en curso (carrier services). Polleamos hasta que esté resuelto en
      // vez de esperar un tiempo fijo: las tarifas de zona salen al primer
      // intento y las de carrier tardan lo que tardan, con tope de ~15s.
      for (let attempt = 0; attempt < 20; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 750))

        const response = await fetch('/cart/async_shipping_rates.json' + '?' + shippingParams)

        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.error || error.description || error.message || 'Failed to get shipping rates')
        }

        const data = await response.json()
        if (data.shipping_rates) {
          console.log('[Shipping Estimator] Rates encontrados:', data.shipping_rates)
          return data.shipping_rates
        }
      }

      console.log('[Shipping Estimator] Timeout esperando shipping rates')
      return []

    } catch (error) {
      console.error('Cart error:', error)
      return []
    }
  }
}

window.Cart = Cart

document.addEventListener("DOMContentLoaded", () => {
  Cart.updateCart()
})
